<template>
  <div class="entries-container">
    <div class="page-header">
      <div class="page-header-main">
        <button type="button" class="btn-back" @click="goBack" title="Voltar">
          <i class="fa-solid fa-arrow-left"></i>
        </button>
        <div>
          <h1 class="page-title">
            <i class="fa-solid fa-ticket"></i>
            Histórico de Poules
          </h1>
          <p class="page-subtitle">
            Período: {{ periodLabel }}
            <span v-if="sellerId"> · Vendedor</span>
            <span v-else-if="regionId"> · Região</span>
          </p>
        </div>
      </div>
    </div>

    <div v-if="!accessOk" class="empty-state">
      <div class="empty-state-content">
        <i class="fa-solid fa-ban"></i>
        <h3>Acesso Negado</h3>
        <p>Você não tem permissão para acessar os dados desta consulta. Abra pelo Fechamento com uma região autorizada.</p>
        <button type="button" class="btn-primary" @click="goBack">Voltar</button>
      </div>
    </div>

    <div v-else-if="loading" class="loading-container">
      <div class="loading-spinner">
        <i class="fa-solid fa-spinner fa-spin"></i>
        <span>Carregando apostas...</span>
      </div>
    </div>

    <div v-else class="results-card">
      <div class="card-header">
        <h3 class="card-title">
          <i class="fa-solid fa-table"></i>
          Apostas encontradas
          <span v-if="totalRows > 0" class="table-count">({{ totalRows }})</span>
        </h3>
        <div class="card-actions">
          <div class="table-zoom-controls" title="Zoom da tabela">
            <button
              type="button"
              class="btn-zoom"
              :disabled="tableZoom <= 5"
              @click="decreaseTableZoom"
              title="Diminuir zoom"
            >
              <i class="fa-solid fa-magnifying-glass-minus"></i>
            </button>
            <span class="zoom-label" @click="resetTableZoom" title="Restaurar 100%">
              {{ tableZoom }}%
            </span>
            <button
              type="button"
              class="btn-zoom"
              :disabled="tableZoom >= 100"
              @click="increaseTableZoom"
              title="Aumentar zoom"
            >
              <i class="fa-solid fa-magnifying-glass-plus"></i>
            </button>
          </div>
          <div v-if="totalPages > 1" class="pagination-info">
            <button type="button" class="btn-pagination" :disabled="pageIndex <= 1" @click="searchBets(pageIndex - 1)">
              <i class="fa-solid fa-chevron-left"></i>
            </button>
            <span>{{ pageIndex }} / {{ totalPages }}</span>
            <button type="button" class="btn-pagination" :disabled="pageIndex >= totalPages" @click="searchBets(pageIndex + 1)">
              <i class="fa-solid fa-chevron-right"></i>
            </button>
          </div>
        </div>
      </div>

      <div class="table-container" :style="tableZoomStyle">
        <table class="modern-table">
          <thead>
            <tr>
              <th>NSU</th>
              <th>Valor</th>
              <th>Prêmios</th>
              <th>Vendedor</th>
              <th>Comissão</th>
              <th>Data Aposta</th>
              <th>Transmissão</th>
              <th>Status</th>
            </tr>
          </thead>
          <tbody>
            <tr v-if="bets.length === 0">
              <td colspan="8" class="empty-message">Nenhuma aposta encontrada para o período.</td>
            </tr>
            <tr
              v-for="b in bets"
              :key="b.betId"
              class="data-row"
              :class="{ premiado: b.totalAwarded > 0 }"
            >
              <td>
                <button type="button" class="nsu-link" @click="openDetail(b.betId)">
                  {{ b.nsu }}
                </button>
              </td>
              <td>
                <span class="currency-value">{{ setCurrencyString(b.amount) }}</span>
              </td>
              <td>
                <span :class="['currency-value', { 'currency-positive': b.totalAwarded > 0 }]">
                  {{ b.totalAwarded > 0 ? '🎉 ' : '' }}{{ setCurrencyString(b.totalAwarded) }}
                </span>
              </td>
              <td>{{ b.sellerName }}</td>
              <td>
                <span class="currency-value">{{ setCurrencyString(b.commission) }}</span>
              </td>
              <td>{{ setDateTimeDisplay(b.dateTime) }}</td>
              <td>{{ setDateTimeDisplay(b.dateTimePersisted) }}</td>
              <td>
                <span :class="['status-badge', b.canceled ? 'badge-danger' : 'badge-success']">
                  {{ b.canceled ? 'Cancelada' : 'Emitida' }}
                </span>
              </td>
            </tr>
          </tbody>
        </table>
      </div>
    </div>
  </div>
</template>

<script>
import jsFunctions from "@/services/jsFunctions";
import mainService from "@/services/mainService";
import { handleErrorMessage } from "@/services/config";
import { toast } from "@/composables/useToast";
import { ROLES } from "@/services/permissions";

const TABLE_ZOOM_STORAGE_KEY = "ab.resultsTableZoom";
const TABLE_ZOOM_MIN = 5;
const TABLE_ZOOM_MAX = 100;
const TABLE_ZOOM_STEP = 5;

export default {
  name: "ViewEntriesNumbers",
  data: () => ({
    accessOk: false,
    loading: true,
    bets: [],
    pageIndex: 1,
    totalRows: 0,
    totalPages: 0,
    regionId: null,
    sellerId: null,
    startDate: null,
    endDate: null,
    currentUser: null,
    userResponsibleRegions: [],
    tableZoom: 100,
  }),
  computed: {
    tableZoomStyle() {
      return { zoom: this.tableZoom / 100 };
    },
    isAdmin() {
      return this.currentUser && parseInt(this.currentUser.role) === ROLES.ADMIN;
    },
    periodLabel() {
      if (!this.startDate || !this.endDate) return "-";
      return `${this.setDateDisplay(this.startDate)} à ${this.setDateDisplay(this.endDate)}`;
    },
  },
  async mounted() {
    this.loadTableZoom();
    await this.loadCurrentUser();
    this.parseQueryParams();
    await this.validateAccess();

    if (this.accessOk) {
      await this.searchBets(1);
    } else {
      this.loading = false;
    }
  },
  methods: {
    async loadCurrentUser() {
      try {
        const userData = localStorage.getItem("user");
        if (!userData) {
          toast.error("Usuário não encontrado. Faça login novamente.");
          this.$router.push("/login");
          return;
        }

        this.currentUser = JSON.parse(userData);
        if (!this.isAdmin) {
          await this.loadUserResponsibleRegions();
        }
      } catch (error) {
        this.currentUser = null;
        toast.error("Erro ao carregar dados do usuário");
      }
    },

    async loadUserResponsibleRegions() {
      try {
        if (!this.currentUser) return;
        const response = await mainService.getUserRegions(this.currentUser.id);
        this.userResponsibleRegions = (response.data || []).map((region) => region.id);

        if (
          this.currentUser.regionId &&
          !this.userResponsibleRegions.some((id) => String(id) === String(this.currentUser.regionId))
        ) {
          this.userResponsibleRegions.push(this.currentUser.regionId);
        }
      } catch (error) {
        this.userResponsibleRegions = [];
      }
    },

    parseQueryParams() {
      const query = this.$route.query || {};
      this.regionId = query.regionId || null;
      this.sellerId = query.sellerId || null;
      this.startDate = query.StartDate || query.startDate || null;
      this.endDate = query.EndDate || query.endDate || null;
    },

    async validateAccess() {
      if (!this.currentUser || (!this.regionId && !this.sellerId) || !this.startDate || !this.endDate) {
        this.accessOk = false;
        toast.error("Parâmetros da consulta inválidos.");
        return;
      }

      if (this.isAdmin) {
        this.accessOk = true;
        return;
      }

      if (this.regionId) {
        this.accessOk = this.userResponsibleRegions.some((id) => String(id) === String(this.regionId));
      } else {
        this.accessOk = true;
      }

      if (!this.accessOk) {
        toast.error("Você não tem permissão para acessar os dados desta região!");
      }
    },

    buildQueryParams(pageIndex) {
      const params = new URLSearchParams();
      params.set("StartDate", this.startDate);
      params.set("EndDate", this.endDate);
      params.set("PageIndex", String(pageIndex));

      if (this.regionId) {
        params.set("RegionId", this.regionId);
      }
      if (this.sellerId) {
        params.set("SellerId", this.sellerId);
      }

      return params.toString();
    },

    async searchBets(pageIndex = 1) {
      if (!this.accessOk) return;

      this.loading = true;
      this.pageIndex = pageIndex;

      try {
        const params = this.buildQueryParams(pageIndex);
        const response = this.sellerId
          ? await mainService.searchBetsBySeller(params)
          : await mainService.searchBalanceBets(params);

        const payload = response.data;
        const list = Array.isArray(payload)
          ? payload
          : (payload?.result || payload?.Result || []);

        this.bets = Array.isArray(list)
          ? [...list].sort(
              (a, b) =>
                new Date(b.dateTimePersisted || b.dateTime) -
                new Date(a.dateTimePersisted || a.dateTime)
            )
          : [];

        // ActionResultConverter devolve só o Result; paginação estimada pelo tamanho da página da API (1000)
        this.totalRows = (!Array.isArray(payload) && (payload?.totalRows || payload?.TotalRows))
          ? (payload.totalRows || payload.TotalRows)
          : this.bets.length + ((pageIndex - 1) * 1000);
        this.totalPages = (!Array.isArray(payload) && (payload?.totalPages || payload?.TotalPages))
          ? (payload.totalPages || payload.TotalPages)
          : (this.bets.length >= 1000 ? pageIndex + 1 : pageIndex);
      } catch (error) {
        this.bets = [];
        toast.error(handleErrorMessage(error));
      } finally {
        this.loading = false;
      }
    },

    openDetail(betId) {
      if (!this.accessOk || !betId) {
        toast.error("Você não tem permissão para acessar os detalhes desta aposta!");
        return;
      }

      this.$router.push({
        path: "/entriesnumbersdetail",
        query: { betId: String(betId) },
      });
    },

    goBack() {
      if (window.history.length > 1) {
        this.$router.back();
        return;
      }
      this.$router.push(this.sellerId ? "/sellers/entries" : "/entries");
    },

    loadTableZoom() {
      try {
        const saved = parseInt(localStorage.getItem(TABLE_ZOOM_STORAGE_KEY), 10);
        if (!Number.isNaN(saved) && saved >= TABLE_ZOOM_MIN && saved <= TABLE_ZOOM_MAX) {
          this.tableZoom = saved;
          return;
        }
      } catch {
        // ignore
      }
      this.tableZoom = 100;
    },

    saveTableZoom() {
      localStorage.setItem(TABLE_ZOOM_STORAGE_KEY, String(this.tableZoom));
    },

    decreaseTableZoom() {
      if (this.tableZoom <= TABLE_ZOOM_MIN) return;
      this.tableZoom = Math.max(TABLE_ZOOM_MIN, this.tableZoom - TABLE_ZOOM_STEP);
      this.saveTableZoom();
    },

    increaseTableZoom() {
      if (this.tableZoom >= TABLE_ZOOM_MAX) return;
      this.tableZoom = Math.min(TABLE_ZOOM_MAX, this.tableZoom + TABLE_ZOOM_STEP);
      this.saveTableZoom();
    },

    resetTableZoom() {
      this.tableZoom = 100;
      this.saveTableZoom();
    },

    setCurrencyString(value) {
      return jsFunctions.setCurrencyString(value);
    },

    setDateDisplay(value) {
      return jsFunctions.setDateDisplay(value);
    },

    setDateTimeDisplay(value) {
      return jsFunctions.setDateTimeDisplay(value);
    },
  },
};
</script>

<style scoped>
.page-header-main {
  display: flex;
  align-items: flex-start;
  gap: 0.75rem;
}

.page-title {
  display: flex;
}

.btn-back {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 36px;
  height: 36px;
  margin-top: 0.25rem;
  border: 1px solid var(--border-color);
  border-radius: 8px;
  background: var(--card-background);
  color: var(--text-primary);
  cursor: pointer;
  flex-shrink: 0;
}

.btn-back:hover {
  background: var(--hover-color, rgba(0, 0, 0, 0.04));
}

.loading-container,
.empty-state {
  display: flex;
  justify-content: center;
  padding: 3rem 1rem;
}

.loading-spinner,
.empty-state-content {
  text-align: center;
  color: var(--text-primary);
}

.empty-state-content i {
  font-size: 2.5rem;
  color: var(--text-muted);
  margin-bottom: 0.75rem;
}

.results-card {
  overflow: visible !important;
}

.card-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  gap: 1rem;
  flex-wrap: wrap;
  overflow: visible !important;
}

.table-count {
  color: var(--text-muted);
  font-weight: 500;
  font-size: 0.9rem;
}

.card-actions {
  display: flex;
  align-items: center;
  gap: 0.75rem;
  flex-wrap: wrap;
}

.table-zoom-controls {
  display: inline-flex;
  align-items: center;
  gap: 0.25rem;
  padding: 0.2rem 0.45rem;
  border: 1px solid var(--border-color);
  border-radius: 6px;
  background: var(--bg-secondary, var(--card-background));
}

.btn-zoom {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 28px;
  height: 28px;
  border: none;
  border-radius: 4px;
  background: transparent;
  color: var(--text-primary);
  cursor: pointer;
}

.btn-zoom:disabled {
  opacity: 0.4;
  cursor: not-allowed;
}

.zoom-label {
  min-width: 3rem;
  text-align: center;
  font-size: 0.82rem;
  font-weight: 600;
  color: var(--text-primary);
  cursor: pointer;
  user-select: none;
}

.pagination-info {
  display: inline-flex;
  align-items: center;
  gap: 0.5rem;
  color: var(--text-primary);
}

.btn-pagination {
  width: 32px;
  height: 32px;
  border: 1px solid var(--border-color);
  border-radius: 6px;
  background: transparent;
  color: var(--text-primary);
  cursor: pointer;
}

.btn-pagination:disabled {
  opacity: 0.4;
  cursor: not-allowed;
}

.table-container {
  overflow-x: auto;
}

.modern-table {
  width: 100%;
  border-collapse: collapse;
  white-space: nowrap;
}

.modern-table th,
.modern-table td {
  padding: 0.85rem 1rem;
  text-align: center;
  border-bottom: 1px solid var(--border-color);
  color: var(--text-primary);
}

.modern-table th {
  background: #e5e5e5;
  font-weight: 600;
}

[data-theme="dark"] .modern-table th {
  background: #2d2d2d;
}

.data-row:nth-child(even) {
  background: rgba(0, 0, 0, 0.02);
}

[data-theme="dark"] .data-row:nth-child(even) {
  background: rgba(255, 255, 255, 0.02);
}

.data-row:hover {
  background: rgba(96, 165, 250, 0.1);
}

.nsu-link {
  border: none;
  background: transparent;
  color: var(--accent-color, #3b82f6);
  font-weight: 600;
  cursor: pointer;
  text-decoration: underline;
}

.nsu-link:hover {
  color: var(--accent-hover, #2563eb);
}

.currency-value {
  font-family: 'Consolas', 'Monaco', monospace;
  font-weight: 500;
}

.currency-value.currency-positive,
.premiado td {
  color: #1d31b2;
}

[data-theme="dark"] .currency-value.currency-positive,
[data-theme="dark"] .premiado td {
  color: #93c5fd;
}

.status-badge {
  display: inline-flex;
  padding: 0.2rem 0.55rem;
  border-radius: 999px;
  font-size: 0.8rem;
  font-weight: 600;
}

.badge-success {
  background: rgba(34, 197, 94, 0.15);
  color: #16a34a;
}

.badge-danger {
  background: rgba(239, 68, 68, 0.15);
  color: #dc2626;
}

.empty-message {
  padding: 2rem !important;
  color: var(--text-muted);
}

.btn-primary {
  margin-top: 1rem;
  padding: 0.6rem 1rem;
  border: none;
  border-radius: 8px;
  background: var(--accent-color, #3b82f6);
  color: #fff;
  cursor: pointer;
}
</style>
